import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cn } from '../lib/cn';
import { CARD_LABEL_COLOURS } from '../tokens';
import { Tooltip } from './Tooltip';

const SIZES = {
  xs: 'size-5 text-2xs',
  sm: 'size-6 text-2xs',
  md: 'size-8 text-xs',
  lg: 'size-10 text-sm',
  xl: 'size-16 text-lg',
} as const;

export type AvatarSize = keyof typeof SIZES;

/** Up to two initials — "Akash Kumar" → "AK", "Akash" → "A". */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * Deterministic fallback colour.
 *
 * Derived from a stable key (the user id) rather than at random, so the same
 * person is always the same colour in every session and on every device — which
 * is what lets people recognise each other at a glance on a busy board.
 */
function colourFor(key: string): string {
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) | 0;
  }
  const palette = CARD_LABEL_COLOURS;
  const entry = palette[Math.abs(hash) % palette.length];
  return entry ? entry.cssVar : 'var(--color-label-slate)';
}

export interface AvatarProps {
  name: string;
  /** Stable identity key for colour selection. Defaults to `name`. */
  colourKey?: string;
  src?: string | null;
  size?: AvatarSize;
  className?: string;
  /** Show the full name on hover. Off inside AvatarGroup, which labels itself. */
  showTooltip?: boolean;
}

export function Avatar({
  name,
  colourKey,
  src,
  size = 'md',
  className,
  showTooltip = false,
}: AvatarProps) {
  const avatar = (
    <AvatarPrimitive.Root
      className={cn(
        'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full',
        SIZES[size],
        className,
      )}
    >
      {src ? (
        <AvatarPrimitive.Image
          src={src}
          alt={name}
          className="size-full object-cover"
          loading="lazy"
        />
      ) : null}
      <AvatarPrimitive.Fallback
        delayMs={src ? 200 : 0}
        className="flex size-full items-center justify-center font-semibold text-white"
        style={{ backgroundColor: colourFor(colourKey ?? name) }}
      >
        <span aria-hidden="true">{initialsOf(name)}</span>
        <span className="sr-only">{name}</span>
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );

  return showTooltip ? <Tooltip content={name}>{avatar}</Tooltip> : avatar;
}

export interface AvatarGroupProps {
  members: Array<{ id: string; name: string; avatarUrl?: string | null }>;
  max?: number;
  size?: AvatarSize;
  className?: string;
}

/**
 * Overlapping stack of member avatars, as seen on card fronts.
 *
 * The whole group carries one accessible label listing everyone, so a screen
 * reader announces "Assigned: Akash, Priya and 3 others" instead of reading a
 * meaningless run of initials.
 */
export function AvatarGroup({ members, max = 4, size = 'sm', className }: AvatarGroupProps) {
  const visible = members.slice(0, max);
  const overflow = members.length - visible.length;

  const summary =
    members.length === 0
      ? 'No members assigned'
      : `Assigned: ${members.map((member) => member.name).join(', ')}`;

  return (
    <div className={cn('flex items-center', className)} role="group" aria-label={summary}>
      {visible.map((member) => (
        <Avatar
          key={member.id}
          name={member.name}
          colourKey={member.id}
          src={member.avatarUrl ?? null}
          size={size}
          className="-ml-1.5 ring-2 ring-surface-raised first:ml-0"
        />
      ))}
      {overflow > 0 ? (
        <span
          aria-hidden="true"
          className={cn(
            '-ml-1.5 inline-flex items-center justify-center rounded-full',
            'bg-surface-sunken font-semibold text-text-secondary ring-2 ring-surface-raised',
            SIZES[size],
          )}
        >
          +{overflow}
        </span>
      ) : null}
    </div>
  );
}
